import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface DemoAccountSummary {
  totalBalance: number;
  totalInitialBalance: number;
  openPositions: number;
  todayTrades: number;
  activeAccounts: number;
  loading: boolean;
  refetch: () => void;
}

export function useDemoAccountSummary(): DemoAccountSummary {
  const { user } = useAuth();
  const [totalBalance, setTotalBalance] = useState(0);
  const [totalInitialBalance, setTotalInitialBalance] = useState(0);
  const [openPositions, setOpenPositions] = useState(0);
  const [todayTrades, setTodayTrades] = useState(0);
  const [activeAccounts, setActiveAccounts] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchSummary = useCallback(async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    try {
      const [accountsRes, positionsRes, ordersRes] = await Promise.all([
        supabase
          .from('demo_accounts')
          .select('balance, initial_balance')
          .eq('user_id', user.id),
        supabase
          .from('strategy_positions')
          .select('id')
          .eq('user_id', user.id)
          .eq('status', 'open'),
        supabase
          .from('strategy_orders')
          .select('id')
          .eq('user_id', user.id)
          .gte('created_at', new Date(new Date().setHours(0, 0, 0, 0)).toISOString()),
      ]);

      if (accountsRes.data) {
        setActiveAccounts(accountsRes.data.length);
        setTotalBalance(accountsRes.data.reduce((sum, a) => sum + Number(a.balance), 0));
        setTotalInitialBalance(accountsRes.data.reduce((sum, a) => sum + Number(a.initial_balance), 0));
      }

      setOpenPositions(positionsRes.data?.length ?? 0);
      setTodayTrades(ordersRes.data?.length ?? 0);
    } catch (err) {
      console.error('Error fetching demo account summary:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  return { totalBalance, totalInitialBalance, openPositions, todayTrades, activeAccounts, loading, refetch: fetchSummary };
}
